/**
 * PostHog for the app.
 *
 * Configured much more tightly than the marketing site because every
 * authenticated page here renders merchant financials:
 *   - session recording off — it would capture revenue figures
 *   - autocapture off — element text includes customer and money data
 *   - pageview capture off — app paths carry workspace and entity UUIDs
 *
 * `cross_subdomain_cookie` must match the marketing site, and both must use the
 * same project key, or the landing-page visitor and the signup are two
 * unrelated people and the funnel is meaningless.
 *
 * See bizguard-backend/docs/CAMPAIGN_TRACKING_SPEC.md §7.
 */

import posthog from 'posthog-js';

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://us.i.posthog.com';

let started = false;

export function initAnalytics(): void {
  if (!KEY || started) return;
  started = true;

  posthog.init(KEY, {
    api_host: HOST,
    cross_subdomain_cookie: true,
    person_profiles: 'identified_only',
    disable_session_recording: true,
    autocapture: false,
    capture_pageview: false,
    // `autocapture: false` covers click/element capture and nothing else. These
    // five are separate features that PostHog turns on from *remote* project
    // config, so they load regardless of the option above unless named here.
    // Verified live on 2026-08-31: exception, dead-click and survey scripts were
    // all loading on an authenticated page before this was added.
    //   exceptions   — error messages and stack traces can carry merchant values
    //   dead clicks  — captures element text, i.e. revenue figures
    //   heatmaps     — click coordinates plus element data on financial pages
    //   surveys      — injects UI into the app
    //   performance  — web vitals re-introduce the URLs capture_pageview omits
    capture_exceptions: false,
    capture_dead_clicks: false,
    capture_heatmaps: false,
    disable_surveys: true,
    capture_performance: false,
  });
}

export function track(event: string, properties?: Record<string, unknown>): void {
  if (!KEY || !started) return;
  posthog.capture(event, properties);
}

/**
 * Always the user UUID, never the email. Identifying with the UUID also makes
 * the distinct_id match what backend events key on, so client and server events
 * land on the same person.
 */
export function identifyUser(userId: string): void {
  if (!KEY || !started) return;
  posthog.identify(userId);
}
